/**
 * EmployeeDrawer — premium slide-over drawer for Time Clock employee details.
 *
 * Triggered by clicking an employee row's avatar/name. Animates in from the
 * right with framer-motion, with a translucent backdrop. Mobile: becomes a
 * full-screen sheet with swipe-down to close.
 *
 * Realtime: subscribes to RealtimeService presence so status updates the
 * moment the employee (or any teammate) changes state. Hours numbers
 * recompute on every parent render since they're passed in as props.
 */
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, Phone, Mail, MapPin, Clock, Calendar, DollarSign,
  Edit2, MessageSquare, Eye, TrendingUp, Coffee, Zap,
} from 'lucide-react';
import realtimeService from '../../services/RealtimeService';
import {
  formatHM,
  formatDuration,
  minutesBetween,
  totalBreakMinutes,
  liveWorkedMinutes,
} from '../../utils/timeClockHelpers';
import { getCrewColor, getCrewName } from '../../services/CrewService';

const STATUS_META = {
  working: { color: '#16a34a', dot: '#22c55e', label: 'Working', glow: true },
  on_break: { color: '#f59e0b', dot: '#f59e0b', label: 'On Break' },
  online: { color: '#3b82f6', dot: '#3b82f6', label: 'Online' },
  idle: { color: '#94a3b8', dot: '#94a3b8', label: 'Idle' },
  offline: { color: '#94a3b8', dot: '#cbd5e1', label: 'Offline' },
};

function avatarColor(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `hsl(${h}, 60%, 55%)`;
}
function initials(name = '') {
  return name.split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();
}

export default function EmployeeDrawer({
  open,
  onClose,
  employee,
  entries,             // all today's entries (for this employee's history)
  todayEntry,          // currently active entry if any
  onEdit,
  onMessage,
  onViewSchedule,
  onViewPayroll,
  onViewTimesheet,
  isMobile,
}) {
  const [presence, setPresence] = useState({});
  useEffect(() => {
    if (!open) return undefined;
    return realtimeService.subscribePresence(setPresence);
  }, [open]);

  const liveStatus = useMemo(() => {
    if (!employee) return 'offline';
    // Map presence -> drawer status. Match by employee.email since presence
    // uses user_id from JWT (matches users.id), but on TimeClock our emp.id
    // is the employee record id. Fall back to checking if there's an active
    // clock-in entry.
    const presEntry =
      presence[employee.userId] ||
      Object.values(presence).find((p) => p?.name && p.name === employee.name);
    if (presEntry?.status) return presEntry.status;
    if (todayEntry && !todayEntry.endTime) {
      const hasOpenBreak = (todayEntry.breaks || []).some((b) => !b.endTime);
      return hasOpenBreak ? 'on_break' : 'working';
    }
    return 'offline';
  }, [presence, employee, todayEntry]);

  const stats = useMemo(() => {
    if (!employee) return null;
    const today = (entries || []).filter((e) => e.employeeId === employee.id);
    const todayMins = today.reduce((sum, e) => {
      const gross = e.endTime ? minutesBetween(e.startTime, e.endTime) : liveWorkedMinutes(e);
      const breakM = totalBreakMinutes(e.breaks);
      return sum + Math.max(0, gross - breakM);
    }, 0);
    // Weekly: this week (Sun-Sat) — small inline computation
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekly = (entries || []).filter(
      (e) => e.employeeId === employee.id && new Date(e.startTime) >= weekStart,
    );
    const weeklyMins = weekly.reduce((sum, e) => {
      const gross = e.endTime ? minutesBetween(e.startTime, e.endTime) : liveWorkedMinutes(e);
      return sum + Math.max(0, gross - totalBreakMinutes(e.breaks));
    }, 0);
    const overtime = Math.max(0, weeklyMins - 40 * 60);
    return {
      todayMins,
      weeklyMins,
      overtimeMins: overtime,
      breakMins: todayEntry ? totalBreakMinutes(todayEntry.breaks) : 0,
      history: today
        .slice()
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
        .slice(0, 5),
    };
  }, [entries, employee, todayEntry]);

  if (!employee) return null;
  const meta = STATUS_META[liveStatus] || STATUS_META.offline;
  const crewColor = employee.crewId ? getCrewColor(employee.crewId) : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="employee-drawer-backdrop"
            style={{
              position: 'fixed', inset: 0, zIndex: 10500,
              background: 'rgba(15, 23, 42, 0.45)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
          />

          {/* Drawer */}
          <motion.aside
            data-testid="employee-drawer"
            role="dialog"
            aria-modal="true"
            initial={isMobile ? { y: '100%' } : { x: '100%' }}
            animate={isMobile ? { y: 0 } : { x: 0 }}
            exit={isMobile ? { y: '100%' } : { x: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            drag={isMobile ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (isMobile && info.offset.y > 120) onClose();
            }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: isMobile ? '100%' : 'min(460px, 96vw)',
              maxWidth: '100vw',
              zIndex: 10501,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderTopLeftRadius: isMobile ? 16 : 0,
              borderTopRightRadius: isMobile ? 16 : 0,
              boxShadow: '-20px 0 60px -10px rgba(15,23,42,0.25)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Drag handle on mobile */}
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
                <div style={{ width: 40, height: 4, background: '#cbd5e1', borderRadius: 2 }} />
              </div>
            )}

            {/* Top header — gradient + photo + status */}
            <div
              style={{
                position: 'relative',
                padding: '24px 24px 20px',
                background: `linear-gradient(135deg, ${avatarColor(employee.id)}25 0%, #ffffff 100%)`,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={onClose}
                data-testid="employee-drawer-close"
                aria-label="Close"
                style={{
                  position: 'absolute', top: 16, right: 16,
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(255,255,255,0.85)', border: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.85)'; }}
              >
                <X size={16} />
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ position: 'relative' }}>
                  {employee.photo ? (
                    <img
                      src={employee.photo}
                      alt=""
                      style={{
                        width: 64, height: 64, borderRadius: '50%',
                        objectFit: 'cover',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                        border: '3px solid #fff',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: avatarColor(employee.id), color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                        border: '3px solid #fff',
                      }}
                    >
                      {initials(employee.name)}
                    </div>
                  )}
                  {/* Status dot overlay */}
                  <div
                    title={meta.label}
                    style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 16, height: 16, borderRadius: '50%',
                      background: meta.dot,
                      border: '3px solid #fff',
                      boxShadow: meta.glow ? `0 0 0 4px ${meta.dot}33` : 'none',
                      animation: meta.glow ? 'maidly-pulse 2s ease-in-out infinite' : 'none',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                    {employee.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {employee.role === 'manager' ? 'Manager' : employee.role === 'admin' ? 'Admin' : 'Cleaner'}
                    {crewColor && (
                      <>
                        {'  ·  '}
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '1px 8px', borderRadius: 999,
                            background: `${crewColor}1a`, color: crewColor,
                            border: `1px solid ${crewColor}40`,
                            fontSize: 11, fontWeight: 600,
                          }}
                        >
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: crewColor }} />
                          {getCrewName(employee.crewId)}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Status pill */}
                  <div
                    data-testid="employee-drawer-status"
                    style={{
                      marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 10px', borderRadius: 999,
                      background: `${meta.color}15`, color: meta.color,
                      border: `1px solid ${meta.color}33`,
                      fontSize: 12, fontWeight: 600,
                    }}
                  >
                    <span
                      style={{
                        width: 7, height: 7, borderRadius: '50%', background: meta.dot,
                        animation: meta.glow ? 'maidly-pulse 2s ease-in-out infinite' : 'none',
                      }}
                    />
                    {meta.label}
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 24px' }}>
              {/* Hours grid */}
              <div
                style={{
                  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10, marginBottom: 18,
                }}
              >
                <StatCard
                  icon={<Clock size={14} />}
                  label="Today"
                  value={stats?.todayMins ? formatDuration(stats.todayMins) : '0h 0m'}
                  testid="drawer-stat-today"
                />
                <StatCard
                  icon={<TrendingUp size={14} />}
                  label="This Week"
                  value={stats?.weeklyMins ? formatDuration(stats.weeklyMins) : '0h 0m'}
                  testid="drawer-stat-weekly"
                />
                <StatCard
                  icon={<Coffee size={14} />}
                  label="Break Today"
                  value={stats?.breakMins ? formatDuration(stats.breakMins) : '—'}
                  testid="drawer-stat-break"
                />
                <StatCard
                  icon={<Zap size={14} />}
                  label="Overtime"
                  value={stats?.overtimeMins ? formatDuration(stats.overtimeMins) : '—'}
                  tone={stats?.overtimeMins ? 'warn' : null}
                  testid="drawer-stat-overtime"
                />
              </div>

              {/* Current shift */}
              {todayEntry && (
                <Section title="Current Shift" testid="drawer-current-shift">
                  <KV
                    label="Clocked in"
                    value={todayEntry.startTime ? formatHM(todayEntry.startTime) : '—'}
                  />
                  <KV
                    label="Clocked out"
                    value={todayEntry.endTime ? formatHM(todayEntry.endTime) : 'Still working'}
                  />
                  <KV label="Job" value={todayEntry.job || 'General Work'} />
                  {todayEntry.gpsClockIn && (
                    <div
                      style={{
                        marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 8px', borderRadius: 999,
                        background: 'rgba(16,185,129,0.10)',
                        color: '#059669', border: '1px solid rgba(16,185,129,0.30)',
                        fontSize: 11, fontWeight: 600,
                      }}
                    >
                      <MapPin size={11} /> GPS verified
                    </div>
                  )}
                </Section>
              )}

              {/* Contact */}
              <Section title="Contact">
                <KV
                  label={<><Mail size={12} style={{ marginRight: 4 }} />Email</>}
                  value={employee.email || '—'}
                  copy={employee.email}
                />
                <KV
                  label={<><Phone size={12} style={{ marginRight: 4 }} />Phone</>}
                  value={employee.phone || '—'}
                  copy={employee.phone}
                />
                <KV
                  label={<><DollarSign size={12} style={{ marginRight: 4 }} />Hourly rate</>}
                  value={employee.hourlyRate ? `$${employee.hourlyRate}/hr` : '—'}
                />
              </Section>

              {/* Recent activity */}
              {stats?.history?.length > 0 && (
                <Section title="Recent Activity" testid="drawer-history">
                  {stats.history.map((h) => (
                    <div
                      key={h.id}
                      style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '8px 0', borderBottom: '1px dashed #e5e7eb',
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: '#374151' }}>
                        {h.endTime ? 'Clocked out' : 'Clocked in'}
                        {h.job && <span style={{ color: '#94a3b8' }}> · {h.job}</span>}
                      </span>
                      <span style={{ color: '#64748b' }}>
                        {formatHM(h.endTime || h.startTime)}
                      </span>
                    </div>
                  ))}
                </Section>
              )}

              {/* Action buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 18 }}>
                <ActionButton
                  icon={<Edit2 size={14} />}
                  label="Edit Employee"
                  onClick={onEdit}
                  testid="drawer-action-edit"
                />
                <ActionButton
                  icon={<MessageSquare size={14} />}
                  label="Message"
                  onClick={onMessage}
                  testid="drawer-action-message"
                />
                <ActionButton
                  icon={<Calendar size={14} />}
                  label="Schedule"
                  onClick={onViewSchedule}
                  testid="drawer-action-schedule"
                />
                <ActionButton
                  icon={<DollarSign size={14} />}
                  label="Payroll"
                  onClick={onViewPayroll}
                  testid="drawer-action-payroll"
                />
              </div>

              <button
                data-testid="drawer-action-view-full"
                onClick={onViewTimesheet || onEdit}
                style={{
                  marginTop: 10, width: '100%',
                  padding: '11px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  boxShadow: '0 4px 12px rgba(0,115,234,0.30)',
                  transition: 'transform 0.12s, box-shadow 0.12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,115,234,0.40)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,115,234,0.30)';
                }}
              >
                <Eye size={14} /> {onViewTimesheet ? 'View Full Timesheet' : 'View Full Profile'}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

// --- small subcomponents ---

function StatCard({ icon, label, value, tone, testid }) {
  const isWarn = tone === 'warn';
  return (
    <div
      data-testid={testid}
      style={{
        padding: '10px 12px', borderRadius: 10,
        background: isWarn ? 'rgba(245,158,11,0.08)' : '#f8fafc',
        border: `1px solid ${isWarn ? 'rgba(245,158,11,0.25)' : '#e5e7eb'}`,
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 11, color: isWarn ? '#b45309' : '#64748b',
          fontWeight: 500, marginBottom: 4,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}
      >
        {icon} {label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: isWarn ? '#b45309' : '#0f172a' }}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid} style={{ marginTop: 18 }}>
      <div
        style={{
          fontSize: 11, color: '#64748b',
          fontWeight: 600, marginBottom: 8,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, copy }) {
  const handleCopy = (e) => {
    if (!copy) return;
    e.stopPropagation();
    navigator.clipboard
      ?.writeText(copy)
      .then(() => {
        try {
          // Lazy import keeps the drawer dependency-light when no copy
          import('sonner').then(({ toast }) => toast.success('Copied to clipboard'));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {});
  };
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 0', borderBottom: '1px dashed #e5e7eb',
        fontSize: 13,
      }}
    >
      <span style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center' }}>{label}</span>
      <span
        onClick={handleCopy}
        style={{
          color: '#0f172a', fontWeight: 500,
          cursor: copy ? 'pointer' : 'default',
          maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        title={copy ? 'Click to copy' : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function ActionButton({ icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: '10px 12px', borderRadius: 10,
        background: '#fff', border: '1px solid #e5e7eb',
        color: '#1f2937', cursor: 'pointer',
        fontSize: 12, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        transition: 'all 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#0073ea';
        e.currentTarget.style.color = '#0073ea';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.color = '#1f2937';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {icon} {label}
    </button>
  );
}
